import React, { useState } from 'react';
import { Button, Typography } from 'antd';
import { CalendarOutlined, EnvironmentOutlined } from '@ant-design/icons';
// Types
import {
  GetCampaignOffers_getCampaignOffers_entities,
  GetCampaignOffers_getCampaignOffers_entities_deliverablesV2,
} from 'api/campaignV2/types/GetCampaignOffers';
import {
  CbDeliverableItemV2Status,
  DeliverableReviewAction,
} from 'api/graphql-global-types';
// Helpers
import {
  formatAppearanceDate,
  getAppearanceDateValue,
} from 'helpers/deliverables';
// Hooks
import { useReviewDeliverables } from 'hooks/useReviewDeliverables';
// UI
import { errorNotification, successNotification } from 'ui/Notification';
// Styles
import styles from './AppearanceReviewSection.module.scss';

const { Text, Title } = Typography;

type AppearanceReviewSectionProps = {
  deliverable: GetCampaignOffers_getCampaignOffers_entities_deliverablesV2;
  offer: GetCampaignOffers_getCampaignOffers_entities;
  onClose: () => void;
  refetchOffers: () => void;
};

const AppearanceReviewSection: React.FC<AppearanceReviewSectionProps> = ({
  deliverable,
  offer,
  onClose,
  refetchOffers,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { reviewDeliverables } = useReviewDeliverables();

  const task =
    deliverable.task?.__typename === 'CampaignAppearanceTask'
      ? deliverable.task
      : null;
  const title = deliverable.task?.title || 'Appearance';
  const description = deliverable.task?.description;
  const location = task?.location;
  const appearanceDate = getAppearanceDateValue(deliverable);

  const itemsToApprove = (deliverable.items || []).filter(
    (item) => item.status === CbDeliverableItemV2Status.ON_BRAND_REVIEW
  );
  const isApproved =
    deliverable.items?.length > 0 &&
    deliverable.items.every(
      (item) => item.status === CbDeliverableItemV2Status.APPROVED
    );

  const handleVerify = async () => {
    if (itemsToApprove.length === 0) return;

    setIsSubmitting(true);
    try {
      await reviewDeliverables({
        variables: {
          input: {
            offerId: offer.id,
            deliverableItems: itemsToApprove.map((item) => ({
              deliverableItemId: item.id,
              action: DeliverableReviewAction.APPROVE,
            })),
          },
        },
      });
      successNotification('Appearance verified as completed');
      setIsSubmitting(false);
      onClose();
      refetchOffers();
    } catch (error: any) {
      errorNotification(
        error?.message || 'Something went wrong, please try again later'
      );
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.appearanceSection}>
      <div className={styles.sectionHeader}>
        <Title level={4}>Appearance</Title>
      </div>

      <div className={styles.details}>
        <Title level={5} className={styles.title}>
          {title}
        </Title>

        {description && (
          <Text type="secondary" className={styles.description}>
            {description}
          </Text>
        )}

        {location && (
          <div className={styles.detailRow}>
            <EnvironmentOutlined className={styles.icon} />
            <Text strong>Location:</Text>
            <Text>{location}</Text>
          </div>
        )}

        <div className={styles.detailRow}>
          <CalendarOutlined className={styles.icon} />
          <Text strong>Appearance Date:</Text>
          <Text>{formatAppearanceDate(appearanceDate)}</Text>
        </div>
      </div>

      <div className={styles.actions}>
        {isApproved ? (
          <Text type="success">Appearance verified as completed</Text>
        ) : (
          <Button
            type="primary"
            loading={isSubmitting}
            disabled={itemsToApprove.length === 0}
            onClick={handleVerify}
          >
            Verify Completed
          </Button>
        )}
      </div>
    </div>
  );
};

export default AppearanceReviewSection;
